import { useState } from "react";
import type { InvestorProfile } from "../../types/entities";
import type { PropertyRecord } from "../../types/portfolio";
import { calculateMarginalRate } from "../../lib/calculations";
import { NumberInput } from "../ui/NumberInput";

const LS_CLEAR_KEYS = [
  "ppa:properties",
  "ppa:profile",
  "ppa:budget",
  "ppa:structures",
  "ppa:settings",
];

interface WizardProps {
  onComplete: (data: { profile: InvestorProfile; property?: PropertyRecord }) => void;
  onSkip: () => void;
  onOpenImport?: () => void;
}

export default function OnboardingWizard({ onComplete, onSkip, onOpenImport }: WizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [skippedProperty, setSkippedProperty] = useState(false);

  // Step 1 — income
  const [name, setName]     = useState("");
  const [income, setIncome] = useState("");

  // Step 2 — first property
  const [nicknameError, setNicknameError] = useState(false);
  const [propNickname, setPropNickname] = useState("");
  const [propAddress, setPropAddress]   = useState("");
  const [propValue, setPropValue]       = useState("");
  const [propRent, setPropRent]         = useState("");
  const [propLoan, setPropLoan]         = useState("");

  // Step 3 — goal
  const [goalType, setGoalType]         = useState<"passive-income" | "portfolio-value">("passive-income");
  const [targetAmount, setTargetAmount] = useState("");
  const [targetYear, setTargetYear]     = useState("");

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSkipAll() {
    localStorage.setItem("ppa:onboarded", "true");
    onSkip();
  }

  function handleSkipProperty() {
    setSkippedProperty(true);
    setStep(3);
  }

  function handleComplete(skipGoal = false) {
    LS_CLEAR_KEYS.forEach((k) => localStorage.removeItem(k));
    localStorage.setItem("ppa:onboarded", "true");

    const annualIncome = Math.round(parseFloat(income) || 0);

    const profile: InvestorProfile = {
      preferredName: name.trim() || "Investor",
      fullLegalName: name.trim() || "Investor",
      annualPreTaxIncome: annualIncome,
      employmentType: "payg",
      marginalTaxRate: calculateMarginalRate(annualIncome),
      strategyTone: "Balanced",
      householdMembers: [],
      reducedIncomePeriods: [],
      verificationDocuments: [],
      taxDocuments: [],
      payslips: [],
      relationships: [],
      ...(!skipGoal && targetAmount.trim()
        ? {
            ...(goalType === "passive-income"
              ? { passiveIncomeTarget: Math.round(parseFloat(targetAmount) || 0) }
              : { portfolioValueTarget: Math.round(parseFloat(targetAmount) || 0) }),
            ...(targetYear.trim()
              ? { portfolioTargetDate: `${targetYear.trim()}-06-30` }
              : {}),
          }
        : {}),
    };

    let property: PropertyRecord | undefined;
    if (!skippedProperty && propAddress.trim()) {
      property = {
        id: crypto.randomUUID(),
        nickname: propNickname.trim() || propAddress.split(",")[0].trim() || "My Property",
        address: propAddress.trim(),
        suburb: "",
        state: "",
        postcode: "",
        assetClass: "Residential",
        propertyType: "House",
        purchaseDate: "",
        purchasePrice: 0,
        buyCosts: 0,
        legalBuyCosts: 0,
        currentValue: Math.round(parseFloat(propValue) || 0),
        weeklyRent: Math.round(parseFloat(propRent) || 0),
        loanBalance: Math.round(parseFloat(propLoan) || 0),
        structureId: "",
        owner: "",
        lender: "",
        loanAccountNumber: "",
        offsetAccount: "",
        loanStartDate: "",
        loanTermYears: 30,
        ioTermYears: 0,
        rate: 0,
        repaymentType: "P&I",
        rateType: "Variable",
        monthlyExpenses: 0,
        annualRates: 0,
        annualInsurance: 0,
        annualStrata: 0,
        annualLandTax: 0,
        annualMaintenance: 0,
        annualPropertyManagement: 0,
        personalTopUp: 0,
        estimatedSellingCostsPct: 2.5,
        customExpenses: [],
        customTabs: [],
        notes: "",
      };
    }

    onComplete({ profile, property });
  }

  // ── Shared input/label styles ──────────────────────────────────────────────

  const labelCls = "block text-sm font-medium text-neutral-700 mb-1.5";
  const inputCls =
    "w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-300";
  const dollarInputCls =
    "w-full rounded-xl border border-neutral-200 bg-white pl-8 pr-3.5 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-300";

  // ── Progress dots ──────────────────────────────────────────────────────────

  const progressDots = (
    <div className="flex items-center gap-1.5">
      {([1, 2, 3] as const).map((n) => (
        <div
          key={n}
          className={`rounded-full transition-all duration-200 ${
            n === step
              ? "w-6 h-2 bg-neutral-900"
              : n < step
              ? "w-2 h-2 bg-neutral-900"
              : "w-2 h-2 bg-neutral-200"
          }`}
        />
      ))}
    </div>
  );

  // ── Step content ───────────────────────────────────────────────────────────

  const step1 = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold text-neutral-900">Welcome.</h2>
        <p className="mt-1.5 text-sm text-neutral-500">
          Let's personalise the app for you. Takes about 60 seconds.
        </p>
      </div>
      <div>
        <label className={labelCls}>What should we call you?</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Alex"
          className={inputCls}
          autoFocus
        />
      </div>
      <div>
        <label className={labelCls}>Your gross annual income</label>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none">
            $
          </span>
          <NumberInput
            value={income}
            onChange={(e) => setIncome(e.target.value)}
            placeholder="120000"
            min="0"
            className={dollarInputCls}
          />
        </div>
        <p className="mt-1.5 text-xs text-neutral-400">
          Used to calculate your marginal tax rate and buying capacity.
        </p>
      </div>
    </div>
  );

  const step2 = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold text-neutral-900">First property</h2>
        <p className="mt-1.5 text-sm text-neutral-500">
          Add your first investment property. You can fill in the details later.
        </p>
        {onOpenImport && (
          <button
            type="button"
            onClick={onOpenImport}
            className="mt-3 flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Import from spreadsheet or document instead
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Nickname</label>
          <input
            type="text"
            value={propNickname}
            onChange={(e) => { setPropNickname(e.target.value); if (e.target.value.trim()) setNicknameError(false); }}
            placeholder="e.g. Kellyville"
            className={inputCls}
          />
          {nicknameError && (
            <p className="mt-1.5 text-xs text-red-600">Please enter a nickname for this property</p>
          )}
        </div>
        <div>
          <label className={labelCls}>Address</label>
          <input
            type="text"
            value={propAddress}
            onChange={(e) => setPropAddress(e.target.value)}
            placeholder="42 Example St, Sydney NSW"
            className={inputCls}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Current value</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none">$</span>
            <NumberInput
              value={propValue}
              onChange={(e) => setPropValue(e.target.value)}
              placeholder="750000"
              min="0"
              className={dollarInputCls}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Weekly rent</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none">$</span>
            <NumberInput
              value={propRent}
              onChange={(e) => setPropRent(e.target.value)}
              placeholder="550"
              min="0"
              className={dollarInputCls}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Loan balance</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none">$</span>
            <NumberInput
              value={propLoan}
              onChange={(e) => setPropLoan(e.target.value)}
              placeholder="500000"
              min="0"
              className={dollarInputCls}
            />
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={handleSkipProperty}
        className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
      >
        Skip this step →
      </button>
    </div>
  );

  const step3 = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold text-neutral-900">Your goal</h2>
        <p className="mt-1.5 text-sm text-neutral-500">
          What does success look like for you?
        </p>
      </div>
      <div>
        <label className={labelCls}>Goal type</label>
        <div className="grid grid-cols-2 gap-3">
          {(
            [
              { value: "passive-income", label: "Passive income target" },
              { value: "portfolio-value", label: "Portfolio value target" },
            ] as const
          ).map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setGoalType(value)}
              className={`rounded-xl border px-4 py-3 text-sm font-medium text-left transition-colors ${
                goalType === value
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>
            {goalType === "passive-income" ? "Monthly passive income" : "Portfolio value"}
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none">$</span>
            <NumberInput
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              placeholder={goalType === "passive-income" ? "10000" : "3000000"}
              min="0"
              className={dollarInputCls}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Target year</label>
          <NumberInput
            value={targetYear}
            onChange={(e) => setTargetYear(e.target.value)}
            placeholder="2035"
            min={new Date().getFullYear()}
            max="2100"
            className={inputCls}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={() => handleComplete(true)}
        className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
      >
        Skip this step →
      </button>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-8 pb-0">
          <span className="text-sm font-semibold tracking-tight text-neutral-900">
            AusProp
          </span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-400">Step {step} of 3</span>
            {progressDots}
          </div>
        </div>

        {/* Step body */}
        <div className="px-8 pt-8 pb-2">
          {step === 1 && step1}
          {step === 2 && step2}
          {step === 3 && step3}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-6">
          <button
            type="button"
            onClick={handleSkipAll}
            className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            Skip setup
          </button>

          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep((prev) => (prev - 1) as 1 | 2 | 3)}
                className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors px-1"
              >
                ← Back
              </button>
            )}
            {step < 3 ? (
              <button
                type="button"
                onClick={() => {
                  if (step === 2 && !propNickname.trim()) {
                    setNicknameError(true);
                    return;
                  }
                  setStep((prev) => (prev + 1) as 1 | 2 | 3);
                }}
                className="bg-neutral-900 text-white text-sm px-6 py-2.5 rounded-full font-medium hover:bg-neutral-700 transition-colors"
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleComplete(false)}
                className="bg-neutral-900 text-white text-sm px-6 py-2.5 rounded-full font-medium hover:bg-neutral-700 transition-colors"
              >
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
